import {ChangeDetectionStrategy, Component, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AppContext} from "../../../app-context";
import {
  BezoekschipOrganisation,
  FacetValueResult,
  FindCargoDeclarants,
  GetVisit,
  IE3ContainerProcessSummary,
  IE3CreateConsignmentFromRepositionedContainers,
  IE3CreateConsignmentFromRepositionedContainersResult,
  IE3FindContainers,
  Port,
  Terminal,
  Visit,
  VisitSummary
} from "@portbase/bezoekschip-service-typescriptmodels";
import {openConfirmationModalWithCallback, openEditModal, sendCommandAndWait, sendQuery} from "../../../common/utils";
import {WebsocketService} from "../../../common/websocket.service";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";
import {ActivatedRoute, Router} from "@angular/router";
import lodash from "lodash";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";
import {DataQuery, FacetedOverview} from "../../common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../../common/overview/overview.component";
import {QuickView} from "../../common/facets/facet-filter/facet-filter.component";
import {ConsignmentUtils} from "../../consignments/consignment.utils";
import {ContainerUtils} from "../container.utils";
import {Handler} from "../../../common/handlers/handler";
import {HandleQuery} from "../../../common/handlers/handle-query";
import {Observable, of} from "rxjs";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";
import {DateFieldRange} from "../../../common/date/date-range/date-field-range";
import {NavigationItem} from "../../common/overview-navigation-tabs/overview-navigation-tabs.component";
import {
  MasterConsignmentDetailsComponent,
  MasterConsignmentDetailsComponentData
} from "../../consignments/details/master-consignment-details/master-consignment-details.component";

const quickViews: QuickView[] = [{
  code: "ALL_EQUIPMENT",
  name: "All equipment",
  icon: "fa-calendar-days",
  hidden: () => AppContext.isAdmin() && AppContext.isProduction(),
  facets: {}
}, {
  code: "ACTION_REQUIRED",
  name: "Attention required",
  icon: "fa-circle-exclamation",
  facets: {
    actionRequired: ["true"]
  }
}];

@Component({
    selector: 'app-containers-overview',
    templateUrl: './containers-overview.component.html',
    styleUrls: ['./containers-overview.component.scss'],
    providers: [WebsocketService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class ContainersOverviewComponent extends FacetedOverview<IE3ContainerProcessSummary, ContainerOverviewFilters> implements OnInit {
  appContext = AppContext;
  private router = inject(Router);
  protected readonly utils = ConsignmentUtils;
  protected readonly containerUtils = ContainerUtils;

  data: IE3ContainerProcessSummary[] = [];

  @ViewChild("downloadDischargeListModalBody") downloadDischargeListModalBody: TemplateRef<any>;
  @ViewChild("downloadDischargeDifferencesModalBody") downloadDischargeDifferencesModalBody: TemplateRef<any>;
  @ViewChild("createConsignmentFromAttachedContainersModalBody") createConsignmentFromAttachedContainersModalBody: TemplateRef<any>;

  downloadCrn: string;
  downloadVisit: Visit;
  portOfLoading: Port;
  selectablePreviousPorts: Port[];
  selectedDeclarant: BezoekschipOrganisation;
  selectableTerminals: Terminal[];
  selectedTerminal: Terminal;
  protected cargoNavigationTabs: NavigationItem[] = [{
    text: "Equipment",
    routerLink: '/container',
    icon: 'fa-container-storage'
  }];

  protected crnFilterValueFormatter = (visit: VisitSummary | string) =>
    (visit as VisitSummary)?.crn || (visit as string);

  protected cargoDeclarantFilterValueFormatter = (declarant: BezoekschipOrganisation | string) => {
    return (declarant as BezoekschipOrganisation)?.fullName || (declarant as string);
  }

  constructor(private websocket: WebsocketService<IE3ContainerProcessSummary>, route: ActivatedRoute) {
    super(undefined, quickViews, quickViews.find(q => q.code === "ACTION_REQUIRED"));
    route.params.subscribe(params => {
      route.queryParams.subscribe(queryParams => {
        this.filters.overviewFilters.filters.crn = this.downloadCrn = params["crn"];
        this.onCrnChange(this.filters.overviewFilters.filters.crn);
        if (this.filters.overviewFilters.filters.crn) {
          this.filters.overviewFilters.selectedQuickView = this.quickViews[0];
        } else {
          this.setLocalStorageKey("container-overview-filters");
        }
        this.cargoNavigationTabs.unshift({
          text: "Consignments",
          routerLink: `/cargo${this.filters.overviewFilters.filters.crn ? '/' + this.filters.overviewFilters.filters.crn : ''}`,
          icon: 'fa-file-lines'
        });
      });
    });
  }

  protected crnFilterChanged = (visit: VisitSummary | string) => {
    this.filters.overviewFilters.filters.crn = this.crnFilterValueFormatter(visit);
    this.router.navigateByUrl(`/container${this.filters.overviewFilters.filters.crn ? '/' + this.filters.overviewFilters.filters.crn : ''}`)
  }

  protected cargoDeclarantFilterChanged = (declarant: BezoekschipOrganisation | undefined) => {
    this.filters.overviewFilters.filters.cargoDeclarant = declarant;
    this.loadData()
  }

  getDataQuery(term: string): DataQuery<IE3FindContainers> {
    if (!this.filters.overviewFilters.filters.timeRange) {
      this.updateTimeRangeToDefault(term);
    }
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange,
      !term, this.filters.overviewFilters.filters.timeRange);
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<IE3FindContainers>> {
      className: "com.portbase.bezoekschip.common.api.consignments.containers.queries.FindContainers",
      payload: {
        term: term,
        crn: this.filters.overviewFilters.filters.crn,
        cargoDeclarantId: this.filters.overviewFilters.filters.cargoDeclarant?.iamConnectedId,
        dateTimeRange: term ? null : timeRange,
        pagination: { from: this.from, count: this.maxItems },
        facetFilters: this.getFacetFilters()
      }
    };
    return queryFn ? queryFn(query) : query;
  }

  ngOnInit(): void {
    this.websocket.initialise(`/api/ui/containers`,
      (update: IE3ContainerProcessSummary) => this.updateFromWebsocket(update), true);
  }

  renderRecords = (result: IE3ContainerProcessSummary[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (result.length < this.maxItems) {
      this.endReached = true;
    }
    if (!append) {
      this.clearSelection();
    }
    this.data = append ? [...(this.data || []), ...result] : [...result];
  }

  trackByRecord = (index: number, record: IE3ContainerProcessSummary): any => record.containerNumber;

  private updateFromWebsocket(update: IE3ContainerProcessSummary) {
    const index = this.data.findIndex(
      c => c.containerProcessId === update.containerProcessId);
    if (index >= 0) {
      if (update.removed) {
        this.data = [...this.data.slice(0, index), ...this.data.slice(index + 1)];
      } else {
        this.data = [...this.data.slice(0, index), update, ...this.data.slice(index + 1)];
      }
    }
  }

  nameFormatter = (name: string): string => {
    switch (name) {
      case "consignmentDataSummaries/consignmentNumber":
        return "Consignment number";
      case "terminalDataSummary/terminal":
        return "Terminal of discharge";
      case "consignmentDataSummaries/empty":
        return "Empty"
      default:
        return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "consignmentDataSummaries/consignmentNumber":
        return (v) => v;
      case "status":
        return PortvisitUtils.enumFormatter;
      default:
        return SentenceCasePipe.format;
    }
  }

  getHiddenFacets = (): string[] => {
    const hiddenFields: string[] = ["count", "allowedViewers", "terminalDataSummary/portOfLoadingCode", "terminalDataSummary/terminalCode"];
    if (!AppContext.isAdmin()) {
      return hiddenFields.concat(["iamConnectedId"]);
    }
    return hiddenFields;
  }

  findVisits = (term) => ConsignmentUtils.findVisits(term, {
    start: this.filters.overviewFilters.filters.timeRange?.start,
    end: this.filters.overviewFilters.filters.timeRange?.end
  });

  findCargoDeclarants: (term: string) => Observable<BezoekschipOrganisation[]> = term => {
    // minCharacters does not prevent a query when cleared but empty term is not valid for command
    if (term?.length > 0) {
      return sendQuery('com.portbase.bezoekschip.common.api.authorisation.FindCargoDeclarants',
        <FindCargoDeclarants>{term: term});
    }

    return of([]);
  }

  get containerCount(): number {
    return lodash.sum(this.allFacets?.find(f => f.name === "count")?.values
      .map(v => v.count)) || 0
  }

  getSelectedContainers(): IE3ContainerProcessSummary[] {
    return this.data.filter(c => this.isRecordSelected(c));
  }

  get nrOfSelectedContainers(): number {
    return this.getSelectedContainers().length;
  }

  get selectedOverlandedContainers(): IE3ContainerProcessSummary[] {
    return this.getSelectedContainers().filter(s => s.overlanded);
  }

  protected onCrnChange = (crn: string) => {
    this.downloadCrn = crn;
    if (crn) {
      this.getVisit(crn).subscribe((v: Visit) => {
        this.downloadVisit = v;
        this.selectableTerminals = ConsignmentUtils.getTerminals(v);
        this.selectablePreviousPorts = ConsignmentUtils.getPreviousPorts(v);
      });
    } else {
      this.downloadVisit = null;
      this.selectableTerminals = [];
      this.selectablePreviousPorts = [];
    }
  }

  downloadDischargeList = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      const shortName = this.selectedDeclarant?.shortName != null ? this.selectedDeclarant?.shortName : AppContext.userProfile.organisation.shortName
      return ContainerUtils.downloadDischargeList(this.downloadVisit.crn, shortName, this.selectedTerminal.terminalCode);
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Discharge list download",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadDischargeListModalBody
  }, 'static');

  sendDischargeListToTerminal = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      const shortName = this.selectedDeclarant?.shortName != null ? this.selectedDeclarant?.shortName : AppContext.userProfile.organisation.shortName
      return ContainerUtils.sendDischargeListToTerminal(this.downloadVisit.crn, shortName, this.selectedTerminal.terminalCode);
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Send discharge list to terminal",
    confirmText: "Send",
    cancelText: "Close",
    body: this.downloadDischargeListModalBody
  }, 'static');

  private getVisit = (crn: string) =>
    sendQuery("com.portbase.bezoekschip.common.api.visit.GetVisit", <GetVisit>{crn: crn});

  @HandleQuery("getFacetValues", { global: true })
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }

  downloadDischargeDifferences = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      return ContainerUtils.downloadDischargeDifferences(this.downloadCrn);
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Download discharge differences",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadDischargeDifferencesModalBody
  }, 'static');

  createConsignmentFromAttachedContainers = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      sendCommandAndWait("com.portbase.bezoekschip.common.api.consignments.queries.CreateConsignmentFromRepositionedContainers", <IE3CreateConsignmentFromRepositionedContainers>{
        crn: this.downloadCrn,
        cargoDeclarantShortName: this.selectedDeclarant?.shortName || AppContext.userProfile.organisation?.shortName,
        cargoDeclarantIamConnectedId: this.selectedDeclarant?.iamConnectedId || AppContext.userProfile.organisation?.iamConnectedId,
        dischargeTerminalCode: this.selectedTerminal.terminalCode,
        portOfLoading: {
          name: this.portOfLoading.name,
          countryUnCode: this.portOfLoading.countryUnCode,
          euLocation: this.portOfLoading.euPort,
          locationUnCode: this.portOfLoading.locationUnCode
        }
      }).subscribe((result: IE3CreateConsignmentFromRepositionedContainersResult) => {
        openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
          consignmentProcessId: result.consignmentProcessId,
        }, {
          warnOnClose: true,
          currentToStack: true
        });
      });
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Attach empty containers",
    confirmText: "Create consignment",
    cancelText: "Close",
    modalSize: "lg",
    body: this.createConsignmentFromAttachedContainersModalBody
  }, 'static');
  protected readonly AppContext = AppContext;
}

interface ContainerOverviewFilters extends FacetedOverviewFilters<ContainerOverviewFacetFilters> {

}

interface ContainerOverviewFacetFilters {
  timeRange?: DateFieldRange;
  crn?: string;
  cargoDeclarant?: BezoekschipOrganisation;
}

